import React from 'react';
import PropTypes from 'prop-types';
import {
  getStarterBoxDryConfig,
  getStarterBoxFreshConfig,
  getStarterBoxProductTypeConfig,
} from '../../products';
import { interpolate } from '../../cms';
import { getAge, getBreed } from '../../pets';
import { PlanTypeSelectionForm } from '../PlanTypeSelectionForm';
import { FreshRecipesSelectionForm } from '../FreshRecipesSelectionForm';
import { DryRecipesSelectionForm } from '../DryRecipesSelectionForm';
import { ACTIONS, levelToLabel, STATE } from './constants';
import {
  getPrices,
  getHighestDailyCalories,
  onCartSubmission,
} from './functions';

const MealPrep = ({
  planSelectionInfo,
  productTypes,
  selectedProductType,
  cartProduct,
  petApi,
  returnToQuiz,
  send,
  service,
}) => {
  const {
    defaultProductType,
    unavailableProductTypes,
    subscriptionProducts,
  } = getStarterBoxProductTypeConfig(
    productTypes,
    cartProduct,
    selectedProductType,
  );

  const onSubmit = async mealPrepType => {
    send(ACTIONS.selectedMealprep({ mealPrepType }));
    return onCartSubmission(service);
  };

  return (
    <PlanTypeSelectionForm
      {...interpolate(planSelectionInfo, {
        pupName: petApi.pet.name,
        pupCalories: parseInt(
          getHighestDailyCalories(subscriptionProducts),
          10,
        ),
      })}
      productTypes={interpolate(planSelectionInfo.recipes, {
        pupName: petApi.pet.name,
        mixedPricePerWeek: productTypes.mixed.recommended?.price_per_week,
        freshPricePerWeek: productTypes.fresh.recommended?.price_per_week,
        bakedPricePerWeek: productTypes.dry.recommended?.price_per_week,
        mixedDiscountPricePerWeek:
          productTypes.mixed.recommended?.discounted_price_per_week,
        freshDiscountPricePerWeek:
          productTypes.fresh.recommended?.discounted_price_per_week,
        bakedDiscountPricePerWeek:
          productTypes.dry.recommended?.discounted_price_per_week,
      })}
      defaultProductType={defaultProductType}
      unavailableProductTypes={unavailableProductTypes}
      returnToQuiz={returnToQuiz}
      onSubmit={onSubmit}
    />
  );
};

MealPrep.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  returnToQuiz: PropTypes.func.isRequired,
  productTypes: PropTypes.object.isRequired,
  selectedProductType: PropTypes.string,
  cartProduct: PropTypes.object,
  petApi: PropTypes.object.isRequired,
  send: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
};

const Fresh = ({
  planSelectionInfo,
  productTypes,
  selectedProductType,
  selectedFreshRecipes,
  cartProduct,
  petApi,
  cartApi,
  reporter,
  offer,
  petId,
  setShowModal,
  setLocalSelection,
  send,
  service,
}) => {
  const {
    numStarterBoxWeeks,
    totalPacks,
    defaultRecipes,
    suggestedVariant,
    unavailableRecipes,
  } = getStarterBoxFreshConfig(
    productTypes[selectedProductType],
    cartProduct,
    selectedFreshRecipes,
  );

  const { discount, periodPrice, discountedPeriodPrice } = getPrices(
    offer,
    suggestedVariant,
  );

  const onSubmit = async freshRecipes => {
    send(
      ACTIONS.selectedFreshRecipes(
        { recipes: freshRecipes },
        {
          productTypes,
          cartProduct,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );
    return onCartSubmission(service);
  };

  return (
    <FreshRecipesSelectionForm
      {...interpolate(planSelectionInfo, {
        pupName: petApi.pet.name,
        pupWeight: petApi.pet.weight,
        pupAge: getAge(petApi.pet.birth_date),
        pupActivity: levelToLabel[petApi.pet.activity_level],
        pupBreed: getBreed(petApi.pet.breeds),
        pupAllergies: petApi.pet.allergies,
        numStarterBoxWeeks,
      })}
      totalPacks={totalPacks}
      defaultRecipes={defaultRecipes}
      onSubmit={onSubmit}
      // TODO: Remove the prices from here and the form
      discount={discount}
      periodPrice={periodPrice}
      discountedPeriodPrice={discountedPeriodPrice}
      returnToQuiz={() => send(ACTIONS.goBack())}
      unavailableRecipes={unavailableRecipes}
    />
  );
};

Fresh.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  returnToQuiz: PropTypes.func.isRequired,
  productTypes: PropTypes.object.isRequired,
  selectedProductType: PropTypes.string.isRequired,
  selectedFreshRecipes: PropTypes.arrayOf(PropTypes.string),
  cartProduct: PropTypes.object,
  petApi: PropTypes.object.isRequired,
  cartApi: PropTypes.object.isRequired,
  reporter: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  petId: PropTypes.string.isRequired,
  setShowModal: PropTypes.func.isRequired,
  setLocalSelection: PropTypes.func.isRequired,
  send: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
};

const Baked = ({
  planSelectionInfo,
  productTypes,
  selectedProductType,
  selectedDryRecipes,
  cartProduct,
  petApi,
  cartApi,
  reporter,
  offer,
  petId,
  setShowModal,
  setLocalSelection,
  send,
  service,
}) => {
  const {
    numStarterBoxWeeks,
    totalPacks,
    defaultRecipes,
    suggestedVariant,
    unavailableRecipes,
  } = getStarterBoxDryConfig(
    productTypes[selectedProductType],
    cartProduct,
    selectedDryRecipes,
  );

  const { discount, periodPrice, discountedPeriodPrice } = getPrices(
    offer,
    suggestedVariant,
  );

  const onSubmit = async dryRecipes => {
    send(
      ACTIONS.selectedBakedRecipes(
        { recipes: dryRecipes },
        {
          productTypes,
          cartProduct,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );
    return onCartSubmission(service);
  };

  return (
    <DryRecipesSelectionForm
      {...interpolate(planSelectionInfo, {
        pupName: petApi.pet.name,
        pupWeight: petApi.pet.weight,
        pupAge: getAge(petApi.pet.birth_date),
        pupActivity: levelToLabel[petApi.pet.activity_level],
        pupBreed: getBreed(petApi.pet.breeds),
        pupAllergies: petApi.pet.allergies,
        numStarterBoxWeeks,
      })}
      totalPacks={totalPacks}
      discount={discount}
      defaultRecipes={defaultRecipes}
      onSubmit={onSubmit}
      periodPrice={periodPrice}
      discountedPeriodPrice={discountedPeriodPrice}
      returnToQuiz={() => send(ACTIONS.goBack())}
      unavailableRecipes={unavailableRecipes}
    />
  );
};

Baked.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  returnToQuiz: PropTypes.func.isRequired,
  productTypes: PropTypes.object.isRequired,
  selectedProductType: PropTypes.string.isRequired,
  selectedDryRecipes: PropTypes.arrayOf(PropTypes.string),
  cartProduct: PropTypes.object,
  petApi: PropTypes.object.isRequired,
  cartApi: PropTypes.object.isRequired,
  reporter: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  petId: PropTypes.string.isRequired,
  setShowModal: PropTypes.func.isRequired,
  setLocalSelection: PropTypes.func.isRequired,
  send: PropTypes.func.isRequired,
  service: PropTypes.object.isRequired,
};

export const components = {
  [STATE.mealprep]: MealPrep,
  [STATE.fresh]: Fresh,
  [STATE.baked]: Baked,
  [STATE.checkout]: ({ finishPlanSelection }) => finishPlanSelection(),
};
